import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, getAuth, saveAuth, rememberArea, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { useTenant } from "./TenantRoot";
import { addDays, dayParts, formatDay, localDay, newRequestId, toMinutes } from "@/lib/slots";

// Used only if the business's own slots did not arrive (an older backend).
const DEFAULT_SLOTS = ["08:00-09:00","09:00-10:00","10:00-11:00","11:00-12:00","13:00-14:00","15:00-16:00","17:00-18:00","18:00-19:00"];
// How many days of availability are asked for at a time.
const WINDOW = 14;

// A booking that is being filled in is kept for this tab, so that signing in
// half way (which leaves this screen) brings the customer back to where they
// were, with everything they entered, instead of to an empty first step.
const draftKey = (slug, serviceId) => `las_book_draft:${slug}:${serviceId}`;
const readDraft = (slug, serviceId) => {
  try {
    const draft = JSON.parse(sessionStorage.getItem(draftKey(slug, serviceId)));
    return draft?.form && draft.form.scheduled_date >= localDay() ? draft : null;
  } catch { return null; }
};
const writeDraft = (slug, serviceId, draft) => {
  try { sessionStorage.setItem(draftKey(slug, serviceId), JSON.stringify(draft)); } catch { /* private window: nothing kept */ }
};
const clearDraft = (slug, serviceId) => { try { sessionStorage.removeItem(draftKey(slug, serviceId)); } catch { /* nothing to clear */ } };

// With a backend that cannot say what is free (an older one): every general
// slot of the coming days, except today's that have started. The server still
// decides when the booking is placed.
const assumedDays = (slots, start) => {
  const now = new Date();
  const minute = now.getHours() * 60 + now.getMinutes();
  return Array.from({ length: WINDOW }, (_, i) => {
    const date = addDays(start || localDay(), i);
    return {
      date, open: true, reason: null,
      slots: slots.map((slot) => {
        const started = date === localDay() && toMinutes(slot.slice(0, 5)) <= minute;
        return { slot, state: started ? "started" : "open", available: !started, left: 1, busy_sellers: [] };
      }),
    };
  });
};

export default function TenantBook() {
  const { slug, serviceId } = useParams();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [service, setService] = useState(null);
  const [missing, setMissing] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [draft] = useState(() => readDraft(slug, serviceId));
  // Sent with the booking: the same order arriving twice (a double tap, a
  // retry after the answer was lost) makes one booking, not two. The id
  // belongs to one exact order (professional, date and time): choosing a
  // different time afterwards is a different order, with an id of its own.
  const [request, setRequest] = useState(() => draft?.request || { id: newRequestId(), order: "" });
  const [step, setStep] = useState(draft?.step || 1);
  const [form, setForm] = useState(draft?.form || {
    seller_id: "",
    scheduled_date: "",
    scheduled_slot: "",
    customer_name: "",
    customer_phone: "",
    address: { line1: "", line2: "", city: "", pincode: "", landmark: "" },
    notes: "",
    payment_mode: "cash",
  });
  const [loading, setLoading] = useState(false);
  // What can be booked, as the server sees it at this moment:
  // [{ date, open, reason, slots: [{ slot, state, available, left, busy_sellers }] }]. null: loading.
  const [days, setDays] = useState(null);
  const [capacity, setCapacity] = useState(1);
  const windowStart = useRef(draft?.form?.scheduled_date || "");

  useEffect(() => {
    api.get(`/public/tenant/${slug}/services/${serviceId}`).then((r) => {
      setService(r.data.service); setSellers(r.data.sellers);
      setForm((f) => (f.seller_id || !r.data.sellers?.[0] ? f : { ...f, seller_id: r.data.sellers[0].id }));
    }).catch(() => setMissing(true));
    const auth = getAuth();
    if (auth?.user) {
      setForm((f) => ({ ...f, customer_name: f.customer_name || auth.user.name || "", customer_phone: f.customer_phone || auth.user.phone || "" }));
    }
  }, [slug, serviceId]);

  const generalSlots = tenant?.booking_slots?.length ? tenant.booking_slots : DEFAULT_SLOTS;
  /** Ask the server what is free from `start` (today when empty). `more`: add to the days already shown. */
  const loadDays = useCallback(async (start, more = false) => {
    if (!more) windowStart.current = start || "";
    try {
      const r = await api.get(`/public/tenant/${slug}/services/${serviceId}/availability`, { params: { days: WINDOW, ...(start ? { start } : {}) } });
      setCapacity(r.data.capacity || 1);
      setDays((shown) => (more && shown ? [...shown, ...r.data.days.filter((d) => !shown.some((x) => x.date === d.date))] : r.data.days));
    } catch {
      setDays((shown) => (more && shown ? [...shown, ...assumedDays(generalSlots, start)] : assumedDays(generalSlots, start)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, serviceId]);
  useEffect(() => { loadDays(windowStart.current); }, [loadDays]);

  useEffect(() => { writeDraft(slug, serviceId, { form, step, request }); }, [slug, serviceId, form, step, request]);

  // A slot can be chosen when it has a place left and the chosen professional is free then.
  const free = (s) => s.available && !(form.seller_id && s.busy_sellers.includes(form.seller_id));
  const dayFree = (d) => d.open && d.slots.some(free);
  const day = days?.find((d) => d.date === form.scheduled_date) || null;
  const slotChosen = !!day && day.open && day.slots.some((s) => s.slot === form.scheduled_slot && free(s));

  // Keep what is selected bookable. First visit: the soonest free day and its
  // first free time. A chosen time that has since been taken is dropped, never
  // silently replaced by another one; past the first step the customer is
  // brought back to choose again.
  useEffect(() => {
    if (!days) return;
    const current = days.find((d) => d.date === form.scheduled_date);
    let slot = form.scheduled_slot;
    let date = form.scheduled_date;
    if (!current) {
      const first = days.find(dayFree);
      date = first ? first.date : "";
      slot = first ? first.slots.find(free).slot : "";
    } else if (slot && !(current.open && current.slots.some((s) => s.slot === slot && free(s)))) {
      slot = "";
    }
    if (date === form.scheduled_date && slot === form.scheduled_slot) return;
    if (step > 1 && form.scheduled_slot) {
      toast.error("That time is no longer free. Please choose another.");
      setStep(1);
    }
    setForm((f) => ({ ...f, scheduled_date: date, scheduled_slot: slot }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, form.scheduled_date, form.scheduled_slot, form.seller_id, step]);

  const chooseDay = (d) => setForm((f) => ({ ...f, scheduled_date: d.date, scheduled_slot: d.slots.find(free)?.slot || "" }));
  // A date typed or picked from the calendar, possibly far ahead: availability is asked for from that date.
  const pickDate = (value) => {
    if (!value) return;
    const shown = days?.find((d) => d.date === value);
    if (shown) { chooseDay(shown); return; }
    setForm((f) => ({ ...f, scheduled_date: value, scheduled_slot: "" }));
    setDays(null);
    loadDays(value);
  };
  const moreDays = () => { if (days?.length) loadDays(addDays(days[days.length - 1].date, 1), true); };

  const auth = getAuth();
  const needsLogin = !auth || auth.user?.role !== "customer" || auth.user?.tenant_id !== service?.tenant_id;
  // Signed in here as the business admin, a seller or the Super Admin, but not
  // as a customer: their number is already verified, so the order is placed
  // with it as a customer. Sending them to the sign-in screen would only take
  // them back to the admin console, and the order would never be placed.
  const staff = needsLogin && [[`t:${slug}:admin`, "the business admin"], [`t:${slug}:seller`, "a seller"], ["super", "the Super Admin"]]
    .map(([scope, label]) => ({ session: getAuth(scope), label })).find((x) => x.session);

  const toAddress = () => {
    if (!slotChosen) { toast.error(day && dayFree(day) ? "Choose a time slot" : "Choose a date that has a free time slot"); return; }
    setStep(2);
  };

  const toPayment = () => {
    if (!form.customer_name.trim() || !form.customer_phone.trim() || !form.address.line1.trim()) {
      toast.error("Enter your name, phone and address");
      return;
    }
    setStep(3);
  };

  const submit = async () => {
    if (needsLogin && !staff) {
      // The draft is kept, so signing in comes back to this step with everything filled in.
      navigate(`/t/${slug}/login?next=/t/${slug}/book/${serviceId}`);
      return;
    }
    setLoading(true);
    const order = `${form.seller_id}|${form.scheduled_date}|${form.scheduled_slot}`;
    const sending = request.order === order ? request : { id: newRequestId(), order };
    if (sending !== request) setRequest(sending);
    try {
      let details = form;
      if (needsLogin) {
        const opened = await api.post("/auth/continue-as-customer", { tenant_slug: slug }, { headers: { Authorization: `Bearer ${staff.session.token}` } });
        saveAuth({ token: opened.data.token, user: opened.data.user });
        rememberArea(`/t/${slug}`);
        details = { ...form, customer_name: form.customer_name || opened.data.user.name || "", customer_phone: form.customer_phone || opened.data.user.phone || "" };
      }
      const r = await api.post(`/customer/${slug}/bookings`, { ...details, service_id: serviceId, client_request_id: sending.id });
      // Mock payment if online
      if (form.payment_mode === "online") {
        await api.post(`/payments/create-order`, { amount: r.data.total });
        await api.post(`/payments/verify`, { booking_id: r.data.id });
      }
      clearDraft(slug, serviceId);
      toast.success(`Booked! ${r.data.code}`);
      navigate(`/t/${slug}/bookings`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401) {
        // The saved sign-in is no longer accepted. The order is kept: sign in and confirm it.
        toast.error("Please sign in again to place this booking");
        navigate(`/t/${slug}/login?next=/t/${slug}/book/${serviceId}`);
      } else if (!e?.response) {
        // No answer. The booking may or may not have been placed: pressing
        // Confirm again is safe, the same request never makes a second booking.
        toast.error("No connection. Check the network and press Confirm again.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Booking failed");
        if ([400, 409].includes(e.response.status)) {
          // The time was taken meanwhile (or is no longer offered): show what is free now.
          setStep(1);
          loadDays(windowStart.current);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (missing) {
    return (
      <div className="p-8 text-center">
        <div className="font-bold">This service is not available.</div>
        <Link to={`/t/${slug}`} className="inline-block mt-4 brand-primary px-6 py-3 rounded-full text-sm font-semibold">Browse services</Link>
      </div>
    );
  }
  if (!service) return <div className="p-8 text-sm brand-text-muted">Loading…</div>;
  const price = service.discount_price || service.price;

  return (
    <div className="pwa-narrow px-5 py-5 md:py-8" data-testid="tenant-book-page">
      <h1 className="brand-heading font-bold text-2xl md:text-3xl">Book service</h1>
      <div className="text-sm brand-text-muted mt-1 break-words">{service.name}</div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mt-5 mb-5" data-testid="tenant-book-stepper" aria-label={`Step ${step} of 3`}>
        {[1,2,3].map((n) => (
          <div key={n} className={`flex-1 h-1.5 rounded-full ${step >= n ? "brand-primary" : "brand-muted"}`} />
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-5" data-testid="tenant-book-step-1">
          {sellers.length > 0 && (
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold brand-text-muted">Choose professional</label>
              <div className="grid gap-2 mt-2 sm:grid-cols-2">
                {sellers.map((s) => (
                  <button key={s.id} type="button" onClick={() => setForm({ ...form, seller_id: s.id })} aria-pressed={form.seller_id === s.id}
                    className="brand-card p-3 flex items-center gap-3 text-left min-w-0"
                    style={{ borderWidth: 2, borderStyle: "solid", borderColor: form.seller_id === s.id ? "hsl(var(--brand-primary))" : "hsl(var(--brand-border))" }}
                    data-testid={`tenant-book-seller-${s.id}`}
                  >
                    <div className="h-10 w-10 rounded-full overflow-hidden brand-muted flex-shrink-0">
                      {s.avatar_url && <img src={s.avatar_url} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold break-words">{s.business_name}</div>
                      <div className="text-[11px] brand-text-muted break-words">{[s.service_area, `★ ${s.rating?.toFixed(1)}`].filter(Boolean).join(" · ")}</div>
                    </div>
                    {form.seller_id === s.id && <Check className="h-5 w-5 brand-primary-text flex-shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-end justify-between gap-3">
              <label className="text-xs uppercase tracking-wider font-semibold brand-text-muted" htmlFor="book-date">Date</label>
              {form.scheduled_date && <span className="text-xs brand-text-muted" data-testid="tenant-book-date-chosen">{formatDay(form.scheduled_date)}</span>}
            </div>
            {!days && <div className="text-sm brand-text-muted mt-2" data-testid="tenant-book-loading-days">Checking what is free…</div>}
            {days && (
              <div className="flex gap-2 mt-2 overflow-x-auto pb-2 -mx-5 px-5 snap-x" data-testid="tenant-book-days">
                {days.filter((d) => d.open).map((d) => {
                  const parts = dayParts(d.date);
                  const on = d.date === form.scheduled_date;
                  const bookable = dayFree(d);
                  return (
                    <button key={d.date} type="button" disabled={!bookable} onClick={() => chooseDay(d)} aria-pressed={on}
                      className={`snap-start flex-shrink-0 w-[68px] py-2 rounded-xl text-center disabled:opacity-40 ${on ? "brand-primary" : "brand-muted brand-text"}`}
                      data-testid={`tenant-book-day-${d.date}`}>
                      <div className="text-[11px] font-semibold uppercase">{parts.weekday}</div>
                      <div className="text-lg font-bold leading-tight">{parts.day}</div>
                      <div className="text-[11px]">{bookable ? parts.month : "Full"}</div>
                    </button>
                  );
                })}
                {/* Nothing to look for after the service's last bookable date. */}
                {!(service.schedule?.available_until && days.length && days[days.length - 1].date >= service.schedule.available_until) && (
                  <button type="button" onClick={moreDays} className="snap-start flex-shrink-0 w-[68px] py-2 rounded-xl text-center brand-surface brand-border border text-xs font-semibold brand-text" data-testid="tenant-book-more-days">
                    More dates
                  </button>
                )}
              </div>
            )}
            <input id="book-date" type="date" aria-label="Pick another date"
              min={service.schedule?.available_from && service.schedule.available_from > localDay() ? service.schedule.available_from : localDay()}
              max={service.schedule?.available_until || undefined}
              value={form.scheduled_date} onChange={(e) => pickDate(e.target.value)}
              className="w-full mt-2 brand-surface brand-text brand-border border rounded-xl px-4 py-3 text-base"
              data-testid="tenant-book-date" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold brand-text-muted">Time slot</label>
            {day && day.open && (
              <div className="grid grid-cols-2 min-[400px]:grid-cols-3 sm:grid-cols-4 gap-2 mt-2" data-testid="tenant-book-slots">
                {day.slots.filter((s) => s.state !== "started").map((s) => {
                  const ok = free(s);
                  const note = !ok ? (s.available ? "Busy" : "Booked") : (capacity > 1 && s.left <= 3 ? `${s.left} left` : "");
                  return (
                    <button key={s.slot} type="button" disabled={!ok} onClick={() => setForm({ ...form, scheduled_slot: s.slot })} aria-pressed={form.scheduled_slot === s.slot}
                      className={`px-2 py-2.5 min-h-[48px] text-xs font-semibold rounded-lg whitespace-nowrap disabled:opacity-40 ${form.scheduled_slot === s.slot ? "brand-primary" : "brand-muted brand-text"}`}
                      data-testid={`tenant-book-slot-${s.slot}`}>
                      <span className={ok ? "" : "line-through"}>{s.slot}</span>
                      {note && <span className="block text-[10px] font-normal" data-testid={`tenant-book-slot-note-${s.slot}`}>{note}</span>}
                    </button>
                  );
                })}
              </div>
            )}
            {days && day && !day.open && <div className="text-sm brand-text-muted mt-2" data-testid="tenant-book-closed">{day.reason}. Choose another date.</div>}
            {days && day && day.open && !dayFree(day) && <div className="text-sm brand-text-muted mt-2" data-testid="tenant-book-no-slots">No free time slots are left on this date. Choose another date.</div>}
            {days && !day && <div className="text-sm brand-text-muted mt-2" data-testid="tenant-book-no-days">No free time slots in these dates. Tap “More dates” to look further ahead.</div>}
          </div>

          <button onClick={toAddress} className="w-full brand-primary py-3.5 rounded-full font-semibold text-sm" data-testid="tenant-book-next-1">Next: Address</button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3" data-testid="tenant-book-step-2">
          <Field label="Your name" autoComplete="name" value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} required testid="tenant-book-name" />
          <Field label="Phone" type="tel" autoComplete="tel" value={form.customer_phone} onChange={(v) => setForm({ ...form, customer_phone: v })} required testid="tenant-book-phone" />
          <Field label="Address line 1" autoComplete="address-line1" value={form.address.line1} onChange={(v) => setForm({ ...form, address: { ...form.address, line1: v } })} required testid="tenant-book-addr1" />
          <Field label="Landmark" value={form.address.landmark} onChange={(v) => setForm({ ...form, address: { ...form.address, landmark: v } })} testid="tenant-book-landmark" />
          <div className="grid grid-cols-2 gap-2">
            <Field label="City" autoComplete="address-level2" value={form.address.city} onChange={(v) => setForm({ ...form, address: { ...form.address, city: v } })} testid="tenant-book-city" />
            <Field label="Pincode" inputMode="numeric" autoComplete="postal-code" value={form.address.pincode} onChange={(v) => setForm({ ...form, address: { ...form.address, pincode: v } })} testid="tenant-book-pincode" />
          </div>
          <Field label="Notes (optional)" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="tenant-book-notes" />
          <div className="flex gap-2 pt-2">
            <button onClick={() => setStep(1)} className="flex-1 brand-muted brand-text py-3.5 rounded-full font-semibold text-sm">Back</button>
            <button onClick={toPayment} className="flex-1 brand-primary py-3.5 rounded-full font-semibold text-sm" data-testid="tenant-book-next-2">Next: Pay</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4" data-testid="tenant-book-step-3">
          <div className="brand-card p-4">
            <div className="text-sm font-bold break-words">{service.name}</div>
            <div className="text-xs brand-text-muted" data-testid="tenant-book-summary-when">{formatDay(form.scheduled_date)} · {form.scheduled_slot}</div>
            <div className="flex justify-between text-sm mt-3 pt-3 border-t brand-border">
              <span>Total</span><span className="font-bold">{formatINR(price)}</span>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold brand-text-muted">Payment</label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {[
                { id: "cash", label: "Cash on Service" },
                { id: "online", label: "Pay Online (Mock)" },
                { id: "upi", label: "UPI on Service" },
                { id: "pay_later", label: "Pay Later" },
              ].map((m) => (
                <button key={m.id} onClick={() => setForm({ ...form, payment_mode: m.id })}
                  className={`px-3 py-3 text-xs font-semibold rounded-xl ${form.payment_mode === m.id ? "brand-primary" : "brand-muted brand-text"}`}
                  data-testid={`tenant-book-pay-${m.id}`}
                >{m.label}</button>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={() => setStep(2)} className="flex-1 brand-muted brand-text py-3.5 rounded-full font-semibold text-sm">Back</button>
            <button onClick={submit} disabled={loading} className="flex-1 brand-primary py-3.5 rounded-full font-semibold text-sm disabled:opacity-50" data-testid="tenant-book-confirm">
              {loading ? "Booking..." : "Confirm booking"}
            </button>
          </div>
          {needsLogin && !staff && <div className="text-xs brand-text-muted text-center" data-testid="tenant-book-signin-note">You'll be asked to sign in (OTP). Your booking details are kept.</div>}
          {staff && <div className="text-xs brand-text-muted text-center break-anywhere" data-testid="tenant-book-staff-note">You are signed in as {staff.label} ({staff.session.user.phone}). The booking is placed with this number as a customer.</div>}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, required, testid, type = "text", inputMode, autoComplete }) {
  return (
    <div className="min-w-0">
      <label className="text-xs uppercase tracking-wider font-semibold brand-text-muted">{label}</label>
      <input
        type={type} inputMode={inputMode} autoComplete={autoComplete}
        value={value} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid}
        className="w-full mt-1 brand-surface brand-text brand-border border rounded-xl px-4 py-3 text-base focus:outline-none"
      />
    </div>
  );
}
